/**
 * The comment button on a row of the change list - issue #99, phase C.
 *
 * It shows how many GitLab threads name the element and, when the element has a
 * page, opens that page at its "Comment on GitLab" section: one component and
 * one route make a thread, whichever door the person came in by.
 *
 * Two rows cannot navigate, and each says why in a tooltip rather than being
 * left to look broken: a REMOVED element is not on this branch, so it has no page
 * here to comment from (its threads are still counted), and a kind the registry
 * has no page for is part of something else's page. The count is shown either way.
 */

import { useInRouterContext, useNavigate } from 'react-router-dom'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import type { ElementChange, Schema } from '../../schema'
import { COMMENT_HASH, countFor, elementLink } from './elementThreads'

interface Props {
  row: ElementChange
  schema: Schema
  /** Threads per element from the one read; empty when it failed. */
  counts: Record<string, number>
}

/**
 * Inside the app there is always a router. A list drawn outside one (a test of
 * a screen that holds it, say) still draws the count; the button just has
 * nowhere to go, rather than the list failing to draw.
 */
export default function ElementCommentButton(props: Props) {
  return useInRouterContext() ? <Navigating {...props} /> : <CommentButton {...props} />
}

function Navigating(props: Props) {
  return <CommentButton {...props} open={useNavigate()} />
}

function CommentButton({ row, schema, counts, open }: Props & { open?: (to: string) => void }) {
  const count = countFor(counts, row)
  // The same rule as the name link beside it, so the two cannot disagree.
  const page = elementLink(schema, row)

  const why =
    row.status === 'removed'
      ? 'This element was removed on this branch, so it has no page here to comment from. Its threads are still counted; GitLab has them.'
      : !page
        ? 'This kind of element has no page of its own in BEST. Comment on the element that contains it.'
        : ''

  const label = count
    ? `Comment on ${row.qualified_name} (${count} ${count === 1 ? 'thread' : 'threads'})`
    : `Comment on ${row.qualified_name}`

  return (
    <Tooltip title={why || 'Comment on GitLab'} describeChild>
      {/* A disabled button swallows the pointer events its tooltip needs. */}
      <span>
        <Button
          size="small"
          aria-label={label}
          disabled={Boolean(why) || !open}
          onClick={() => page && open?.(`${page}${COMMENT_HASH}`)}
          startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
          sx={{ minWidth: 0 }}
        >
          {count || ''}
        </Button>
      </span>
    </Tooltip>
  )
}
